import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Table,
} from 'react-bootstrap';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiFileText,
    FiRefreshCw,
    FiUploadCloud,
    FiXCircle,
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { importStandardScheduledForecastExcel } from '../../services/api';
import {
    ECMWF_IMPORT_ROW_STATUS,
    canConfirmEcmwfImport,
    canRunStandardScheduledForecastImport,
    getAdminRoleFromToken,
    getDryRunProcessableCount,
    getImportDetailRows,
    getStandardScheduledForecastImportErrorMessage,
    validateEcmwfImportFile,
} from '../../utils/ecmwfExcelImport';
import { useTranslation } from '../../hooks/useTranslation';
import './EcmwfV2ExcelImportTab.css';

const PREFIX = 'pages.adminTripsForecastBackupCheck.standardImportExcel';

const STATUS_CONFIG = {
    [ECMWF_IMPORT_ROW_STATUS.VALID]: { labelKey: 'status.valid', bg: 'success' },
    [ECMWF_IMPORT_ROW_STATUS.MATCHED]: { labelKey: 'status.matched', bg: 'success' },
    [ECMWF_IMPORT_ROW_STATUS.CREATED]: { labelKey: 'status.created', bg: 'success' },
    [ECMWF_IMPORT_ROW_STATUS.UPDATED]: { labelKey: 'status.updated', bg: 'primary' },
    [ECMWF_IMPORT_ROW_STATUS.NO_MATCH]: { labelKey: 'status.noMatch', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.AMBIGUOUS_LOCATION]: { labelKey: 'status.ambiguousLocation', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.DUPLICATE_TARGET]: { labelKey: 'status.duplicateTarget', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.AMBIGUOUS_FORECAST]: { labelKey: 'status.ambiguousForecast', bg: 'warning', text: 'dark' },
    [ECMWF_IMPORT_ROW_STATUS.UPSERT_ERROR]: { labelKey: 'status.upsertError', bg: 'danger' },
};

const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes)) return '—';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
};

const statusBadge = (status, t) => {
    const config = STATUS_CONFIG[status];
    if (!config) {
        return <Badge bg="secondary">{status || '—'}</Badge>;
    }
    return (
        <Badge bg={config.bg} text={config.text}>
            {t(`${PREFIX}.${config.labelKey}`)}
        </Badge>
    );
};

const SummaryCard = ({ label, value, variant = 'primary' }) => (
    <Col xs={12} sm={6} xl={3}>
        <Card className={`ecmwf-import-stat ecmwf-import-stat--${variant}`}>
            <Card.Body>
                <div className="text-muted small">{label}</div>
                <div className="ecmwf-import-stat__value">{value ?? 0}</div>
            </Card.Body>
        </Card>
    </Col>
);

const RowMessages = ({ errors, warnings }) => {
    const errorList = Array.isArray(errors) ? errors : [];
    const warningList = Array.isArray(warnings) ? warnings : [];
    if (!errorList.length && !warningList.length) return '—';
    return (
        <div className="ecmwf-import-messages">
            {errorList.map((message, index) => (
                <div className="text-danger" key={`error-${index}`}>{message}</div>
            ))}
            {warningList.map((message, index) => (
                <div className="text-warning-emphasis" key={`warning-${index}`}>{message}</div>
            ))}
        </div>
    );
};

const ResultView = ({ result, title, t }) => {
    const summary = result?.summary || {};
    const rows = useMemo(() => getImportDetailRows(result), [result]);
    const isFinal = result?.dry_run === false;

    if (!result) return null;

    return (
        <div className="mt-4">
            <Alert variant={result.status_code === 200 ? 'success' : 'warning'}>
                <div className="d-flex align-items-start gap-2">
                    {result.status_code === 200
                        ? <FiCheckCircle className="mt-1 flex-shrink-0" aria-hidden />
                        : <FiAlertTriangle className="mt-1 flex-shrink-0" aria-hidden />}
                    <div>
                        <strong>{title}</strong>
                        {result.message && <div className="mt-1">{result.message}</div>}
                        {result.timestamp && (
                            <small className="text-muted d-block mt-1">
                                {new Date(result.timestamp).toLocaleString('fr-FR')}
                            </small>
                        )}
                    </div>
                </div>
            </Alert>

            <Row className="g-3 mb-4">
                <SummaryCard label={t(`${PREFIX}.summary.totalRows`)} value={summary.total_rows} />
                <SummaryCard
                    label={isFinal
                        ? t(`${PREFIX}.summary.forecastsProcessed`)
                        : t(`${PREFIX}.summary.excelRowsMatched`)}
                    value={isFinal
                        ? summary.rows_processed
                        : (summary.excel_rows_matched ?? summary.rows_matched ?? summary.rows_valid)}
                    variant="success"
                />
                {!isFinal && (
                    <SummaryCard
                        label={t(`${PREFIX}.summary.policiesMatched`)}
                        value={summary.policies_matched ?? summary.rows_matched}
                        variant="primary"
                    />
                )}
                {isFinal ? (
                    <>
                        <SummaryCard label={t(`${PREFIX}.summary.created`)} value={summary.rows_created} variant="success" />
                        <SummaryCard label={t(`${PREFIX}.summary.updated`)} value={summary.rows_updated} variant="primary" />
                        <SummaryCard label={t(`${PREFIX}.summary.upsertErrors`)} value={summary.rows_upsert_errors} variant="danger" />
                    </>
                ) : (
                    <>
                        <SummaryCard label={t(`${PREFIX}.summary.noMatch`)} value={summary.rows_no_matching_policy} variant="warning" />
                        <SummaryCard label={t(`${PREFIX}.summary.ambiguousLocation`)} value={summary.rows_ambiguous_location} variant="warning" />
                    </>
                )}
                <SummaryCard label={t(`${PREFIX}.summary.rowsWithErrors`)} value={summary.rows_with_errors} variant="danger" />
                <SummaryCard label={t(`${PREFIX}.summary.warnings`)} value={summary.warnings_count} variant="warning" />
            </Row>

            {isFinal && (
                <Alert variant={summary.email_queued ? 'info' : 'secondary'}>
                    <strong>{t(`${PREFIX}.emailLabel`)}</strong>{' '}
                    {summary.email_queued
                        ? t(`${PREFIX}.emailQueued`)
                        : t(`${PREFIX}.emailNotQueued`)}
                    {summary.email_recipients_count != null
                        ? ` (${summary.email_recipients_count})`
                        : ''}
                </Alert>
            )}

            {rows.length > 0 && (
                <Card>
                    <Card.Header className="fw-semibold">{t(`${PREFIX}.detailsTitle`)}</Card.Header>
                    <Card.Body className="p-0">
                        <div className="ecmwf-import-table-wrap">
                            <Table striped hover responsive className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>{t(`${PREFIX}.colRow`)}</th>
                                        <th>{t(`${PREFIX}.colStatus`)}</th>
                                        <th>{t(`${PREFIX}.colPolicy`)}</th>
                                        <th>{t(`${PREFIX}.colCompany`)}</th>
                                        <th>{t(`${PREFIX}.colReference`)}</th>
                                        <th>{t(`${PREFIX}.colTripDay`)}</th>
                                        <th>{t(`${PREFIX}.colDataSource`)}</th>
                                        <th>{t(`${PREFIX}.colCoords`)}</th>
                                        <th>{t(`${PREFIX}.colAction`)}</th>
                                        <th>{t(`${PREFIX}.colMessages`)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={`${row.excel_row}-${row.status}-${index}`}>
                                            <td><code>{row.excel_row ?? '—'}</code></td>
                                            <td>{statusBadge(row.status, t)}</td>
                                            <td>{row.match?.policy_code || '—'}</td>
                                            <td>
                                                {row.match?.company_name
                                                    || row.row?.company_name
                                                    || row.matching_keys?.company_name
                                                    || '—'}
                                            </td>
                                            <td>
                                                {row.match?.reference_name
                                                    || row.row?.reference_name
                                                    || row.matching_keys?.reference_name
                                                    || '—'}
                                            </td>
                                            <td>
                                                {row.row?.trip_day
                                                    || row.matching_keys?.trip_day
                                                    || '—'}
                                            </td>
                                            <td>
                                                {row.row?.data_source
                                                    || row.matching_keys?.data_source
                                                    || row.match?.data_source
                                                    || '—'}
                                            </td>
                                            <td>
                                                {(row.row?.latitude ?? row.matching_keys?.latitude) != null
                                                    ? `${row.row?.latitude ?? row.matching_keys?.latitude}, ${row.row?.longitude ?? row.matching_keys?.longitude}`
                                                    : '—'}
                                            </td>
                                            <td>{row.action ? statusBadge(row.action, t) : '—'}</td>
                                            <td><RowMessages errors={row.errors} warnings={row.warnings} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

const StandardScheduledForecastExcelImportTab = () => {
    const { t } = useTranslation();
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const role = getAdminRoleFromToken(accessToken || localStorage.getItem('admin_access_token'));
    const canRun = canRunStandardScheduledForecastImport(role);

    const [file, setFile] = useState(null);
    const [validatedFile, setValidatedFile] = useState(null);
    const [fileError, setFileError] = useState(null);
    const [requestError, setRequestError] = useState(null);
    const [dryRunResult, setDryRunResult] = useState(null);
    const [finalResult, setFinalResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const abortRef = useRef(null);
    const requestVersionRef = useRef(0);
    const inputRef = useRef(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    const invalidatePendingRequest = () => {
        requestVersionRef.current += 1;
        abortRef.current?.abort();
        abortRef.current = null;
        setLoading(false);
    };

    const selectFile = (nextFile) => {
        invalidatePendingRequest();
        setRequestError(null);
        setDryRunResult(null);
        setFinalResult(null);
        setValidatedFile(null);
        setShowConfirm(false);
        const validation = validateEcmwfImportFile(nextFile);
        if (!validation.valid) {
            setFile(null);
            setFileError(validation.message);
            return;
        }
        setFile(nextFile);
        setFileError(null);
    };

    const runImport = async (dryRun) => {
        if (!canRun) return;
        const validation = validateEcmwfImportFile(file);
        if (!validation.valid) {
            setFileError(validation.message);
            return;
        }

        const version = requestVersionRef.current + 1;
        requestVersionRef.current = version;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setRequestError(null);
        if (dryRun) {
            setDryRunResult(null);
            setFinalResult(null);
        }

        try {
            const response = await importStandardScheduledForecastExcel(file, dryRun, controller.signal);
            if (requestVersionRef.current !== version || controller.signal.aborted) return;
            if (dryRun) {
                setValidatedFile(file);
                setDryRunResult(response);
            } else {
                setFinalResult(response);
                setShowConfirm(false);
            }
        } catch (error) {
            if (controller.signal.aborted || requestVersionRef.current !== version) return;
            setRequestError(getStandardScheduledForecastImportErrorMessage(error));
            if (!dryRun) setShowConfirm(false);
        } finally {
            if (requestVersionRef.current === version) {
                setLoading(false);
                abortRef.current = null;
            }
        }
    };

    const reset = () => {
        invalidatePendingRequest();
        setFile(null);
        setValidatedFile(null);
        setFileError(null);
        setRequestError(null);
        setDryRunResult(null);
        setFinalResult(null);
        setShowConfirm(false);
        if (inputRef.current) inputRef.current.value = '';
    };

    const canConfirm = canConfirmEcmwfImport({
        dryRunResult,
        validatedFile,
        selectedFile: file,
        loading,
    }) && canRun;
    const processableRows = getDryRunProcessableCount(dryRunResult);
    const totalRows = Number(dryRunResult?.summary?.total_rows || 0);
    const matchedExcelRows = Number(
        dryRunResult?.summary?.excel_rows_matched
        ?? dryRunResult?.summary?.rows_matched
        ?? dryRunResult?.summary?.rows_valid
        ?? 0
    );
    const ignoredRows = Math.max(0, totalRows - matchedExcelRows);

    if (!canRun) {
        return (
            <Alert variant="warning" className="mb-0">
                <FiAlertTriangle className="me-2" aria-hidden />
                {t(`${PREFIX}.forbidden`)}
            </Alert>
        );
    }

    return (
        <div className="ecmwf-import">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h5 className="mb-1">{t(`${PREFIX}.title`)}</h5>
                    <p className="text-muted mb-0">{t(`${PREFIX}.description`)}</p>
                </div>
            </div>

            <div className="fb-endpoint mb-3" role="note">
                <span className="fb-endpoint__method">POST</span>
                <code>/api/standard/trips/scheduled-forecast/import-excel</code>
            </div>

            <Alert variant="light" className="border">
                <strong>{t(`${PREFIX}.columnsTitle`)}</strong>{' '}
                {t(`${PREFIX}.columnsList`)}
                <div className="small text-muted mt-1">{t(`${PREFIX}.columnsHint`)}</div>
            </Alert>

            <div
                className={`ecmwf-import-dropzone${dragActive ? ' is-dragging' : ''}${fileError ? ' is-invalid' : ''}`}
                onDragEnter={(event) => {
                    event.preventDefault();
                    setDragActive(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => {
                    event.preventDefault();
                    if (event.currentTarget === event.target) setDragActive(false);
                }}
                onDrop={(event) => {
                    event.preventDefault();
                    setDragActive(false);
                    selectFile(event.dataTransfer.files?.[0] || null);
                }}
            >
                <FiUploadCloud size={36} aria-hidden />
                <div className="fw-semibold mt-2">{t(`${PREFIX}.dropTitle`)}</div>
                <div className="text-muted small mb-3">{t(`${PREFIX}.dropHint`)}</div>
                <Button
                    type="button"
                    variant="outline-primary"
                    onClick={() => inputRef.current?.click()}
                    disabled={loading}
                >
                    {t(`${PREFIX}.selectFile`)}
                </Button>
                <Form.Control
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="visually-hidden"
                    onChange={(event) => selectFile(event.target.files?.[0] || null)}
                    disabled={loading}
                />
            </div>

            {fileError && <div className="text-danger small mt-2">{fileError}</div>}

            {file && (
                <Card className="mt-3">
                    <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
                        <div className="d-flex align-items-center gap-2">
                            <FiFileText size={22} className="text-primary" aria-hidden />
                            <div>
                                <div className="fw-semibold">{file.name}</div>
                                <div className="text-muted small">{formatFileSize(file.size)}</div>
                            </div>
                        </div>
                        <Button
                            variant="primary"
                            onClick={() => runImport(true)}
                            disabled={loading}
                        >
                            {loading && !finalResult ? (
                                <Spinner size="sm" animation="border" className="me-2" />
                            ) : (
                                <FiCheckCircle className="me-2" />
                            )}
                            {t(`${PREFIX}.validateFile`)}
                        </Button>
                    </Card.Body>
                </Card>
            )}

            {requestError && (
                <Alert variant="danger" className="mt-3" dismissible onClose={() => setRequestError(null)}>
                    <FiXCircle className="me-2" />
                    {requestError}
                    <div className="mt-2">
                        <Button variant="outline-danger" size="sm" onClick={() => runImport(!dryRunResult)}>
                            {t(`${PREFIX}.retry`)}
                        </Button>
                    </div>
                </Alert>
            )}

            <ResultView
                result={dryRunResult}
                title={t(`${PREFIX}.dryRunResultTitle`)}
                t={t}
            />

            {dryRunResult && !finalResult && (
                <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                    <Button variant="outline-secondary" onClick={reset} disabled={loading}>
                        {t(`${PREFIX}.importAnother`)}
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => setShowConfirm(true)}
                        disabled={!canConfirm}
                    >
                        {t(`${PREFIX}.confirmImport`)}
                    </Button>
                </div>
            )}

            <ResultView
                result={finalResult}
                title={t(`${PREFIX}.finalResultTitle`)}
                t={t}
            />

            {finalResult && (
                <div className="d-flex justify-content-end mt-3">
                    <Button variant="outline-primary" onClick={reset}>
                        <FiRefreshCw className="me-2" />
                        {t(`${PREFIX}.importAnother`)}
                    </Button>
                </div>
            )}

            <Modal
                show={showConfirm}
                onHide={() => !loading && setShowConfirm(false)}
                centered
                className="backup-email-confirm-modal"
            >
                <Modal.Header closeButton={!loading}>
                    <Modal.Title>{t(`${PREFIX}.confirmTitle`)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t(`${PREFIX}.confirmMessage`)}</p>
                    <ul className="mb-3">
                        <li>
                            <strong>{processableRows}</strong>
                            {' '}
                            {t(`${PREFIX}.confirmProcessable`)}
                        </li>
                        <li>
                            <strong>{matchedExcelRows}</strong>
                            {' '}
                            {t(`${PREFIX}.confirmMatched`)}
                        </li>
                        <li>
                            <strong>{ignoredRows}</strong>
                            {' '}
                            {t(`${PREFIX}.confirmIgnored`)}
                        </li>
                    </ul>
                    <Alert variant="warning" className="mb-0">
                        {t(`${PREFIX}.confirmWarning`)}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="button"
                        variant="outline-secondary"
                        className="btn fb-btn-secondary"
                        onClick={() => setShowConfirm(false)}
                        disabled={loading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type="button"
                        variant="primary"
                        className="btn fb-btn-primary"
                        onClick={() => runImport(false)}
                        disabled={loading || !canConfirm}
                    >
                        {loading && <Spinner size="sm" animation="border" className="me-2" />}
                        {t(`${PREFIX}.confirmSubmit`)}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StandardScheduledForecastExcelImportTab;
